/** Service-package ids as the backend defines them. The same numbers appear in
 *  `moneyPoolManagement.service_package` and in the fund header's plan badge. */
export const PLAN_SUBSCRIPTION = 1
export const PLAN_FREE = 2
export const PLAN_COMMISSION = 3

/**
 * Copy is written from what Hamyan actually does for a fund manager — keeping
 * the ledger so nobody needs Excel, telling members when to pay, booking their
 * deposits automatically through the gateway, showing members where the money
 * went, and holding the fund's cash in trust.
 *
 * The important thing for the decision: کارمزدی and اشتراکی unlock exactly the
 * same capabilities. They differ only in *who pays and how*. So each tier also
 * states what it does NOT include, rather than three near-identical green lists
 * that made the tiers look interchangeable.
 */
const PAID_CAPABILITIES = [
    'درگاه پرداخت: عضو خودش واریز می‌کند و همان لحظه در دفترها ثبت می‌شود، حتی وقتی شما در دسترس نیستید',
    'پیامک یادآوری حق عضویت و قسط، و رسید فوری برای هر واریز',
    'پول صندوق می‌تواند نزد همیان بماند و وام‌ها خودکار به حساب وام‌گیرنده برود',
    'عضو و وام بدون سقف',
]

/** Ordered cheapest to most capable — the selector reads as a progression. */
export const PLAN_TIERS: IPlanTier[] = [
    {
        servicePackage: PLAN_FREE,
        name: 'رایگان',
        // The price lives in the selector so the cost of each tier is visible
        // before you commit; the old page revealed it only on the next screen.
        priceLabel: 'بدون هزینه',
        headline: 'بدون هزینه',
        priceCaption: '',
        tagline: 'دفتر حساب صندوق، بدون اکسل و بدون کاغذ',
        gains: [
            'حساب‌وکتاب هر عضو و هر وام را همیان نگه می‌دارد؛ شما فقط می‌گویید چه اتفاقی افتاده',
            'اعضا در کارتابل می‌بینند پول کجا رفته و درخواست وام می‌دهند',
            'اطلاعات روی فضای ابری ذخیره و پشتیبان‌گیری می‌شود',
        ],
        limits: [
            'تا ۲۰ عضو و ۲ وام در هر ماه',
            'بدون درگاه پرداخت؛ واریزی‌ها را دستی ثبت می‌کنید',
            'اطلاع‌رسانی فقط با نوتیفیکیشن، بدون پیامک',
        ],
        limitsLabel: 'محدودیت‌ها',
        carried: [],
        carriedLabel: '',
        route: null,
    },
    {
        servicePackage: PLAN_COMMISSION,
        name: 'کارمزدی',
        // Live rule (`CommissionRule` type=prt, read from prod): 2% of the
        // deposit, floored at ۱۰٬۰۰۰ and capped at ۵۰٬۰۰۰ تومان. Do NOT take
        // this from `MoneypoolCommission.calculate_portion_commission` — that
        // stepped 900/1200/1500 path is not what charges these transactions.
        // Real transactions confirm the 2% ratio and the cap.
        priceLabel: '۲٪ هر واریز',
        headline: '۲٪ هر واریز',
        priceCaption: 'حداقل ۱۰٬۰۰۰ و حداکثر ۵۰٬۰۰۰ تومان',
        tagline: 'صندوق هزینه‌ی ماهانه نمی‌دهد؛ کارمزد به واریز هر عضو اضافه می‌شود',
        gains: PAID_CAPABILITIES,
        limits: [
            'عضو بابت هر واریز ۲٪ می‌پردازد؛ کمتر از ۱۰٬۰۰۰ و بیشتر از ۵۰٬۰۰۰ تومان نمی‌شود',
        ],
        limitsLabel: 'هزینه‌ی این نسخه',
        carried: [],
        carriedLabel: '',
        route: 'commission',
    },
    {
        servicePackage: PLAN_SUBSCRIPTION,
        name: 'اشتراکی',
        // Filled from the server — this fund's real monthly price.
        priceLabel: '',
        headline: '',
        priceCaption: 'ماهانه، برای همه‌ی اعضای فعلی صندوق',
        tagline: 'همان امکانات کارمزدی، با این تفاوت که واریز اعضا کارمزد ندارد',
        gains: ['اعضا بابت واریز هیچ کارمزدی نمی‌پردازند'],
        limits: ['هزینه‌ی ماهانه بر عهده‌ی صندوق است، نه اعضا'],
        limitsLabel: 'هزینه‌ی این نسخه',
        carried: PAID_CAPABILITIES,
        carriedLabel: 'همه‌ی امکانات کارمزدی را هم دارد',
        route: 'subscription',
    },
]

export const getTier = (servicePackage: number) =>
    PLAN_TIERS.find((tier) => tier.servicePackage === servicePackage) ??
    PLAN_TIERS[0]
